import React, { useEffect, useRef } from 'react';
import { View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Input from '@/components/General/inputs/Input';
import FieldLabel from './FieldLabel';

/**
 * Renders a `name` schema field (first + last). The payload value is
 * `{ first, last, linkedPersonId? }`.
 *
 * Autofill: when `field.config.autofillLoggedIn` is true and a `loggedInUser`
 * is provided (passed via props from the renderer — never reads the auth store
 * directly), the field pre-fills first/last and sets `linkedPersonId` to the
 * logged-in user's person id once on mount, only if no value is already set.
 */
const NameField = ({ field, value, onChange, disabled, loggedInUser }) => {
    const v = value && typeof value === 'object' ? value : {};
    const config = field.config || {};
    const didAutofill = useRef(false);

    useEffect(() => {
        if(didAutofill.current) return;
        if(disabled || typeof onChange !== 'function') return;
        if(config.autofillLoggedIn === false || !loggedInUser) return;
        // Only autofill when the field is empty.
        const hasValue = v.first || v.last;
        if(hasValue) return;

        didAutofill.current = true;
        onChange({
            first: loggedInUser.firstName ?? '',
            last: loggedInUser.lastName ?? '',
            linkedPersonId: loggedInUser.id ?? null,
        });
    }, [config.autofillLoggedIn, loggedInUser, v.first, v.last, onChange]);

    const update = (key, val) => {
        // Preserve an existing link when the user hasn't cleared the name.
        const next = { ...v, [key]: val };
        if(next.linkedPersonId && (!next.first && !next.last)){
            delete next.linkedPersonId;
        }
        onChange(next);
    };

    return (
        <View style={styles.container}>
            <FieldLabel
                label={field.label}
                required={field.required}
                description={field.description}
                testID={`form-field-${field.fieldId}-label`}
            />
            <View style={styles.row}>
                <View style={styles.halfWidth}>
                    <Input
                        testID={`form-field-${field.fieldId}-first`}
                        title="First Name"
                        placeholder="First"
                        value={v.first ?? ''}
                        onUpdate={(text) => update('first', text)}
                        required={field.required}
                        disabled={disabled}
                        autoCapitalize="words"
                        autoComplete="given-name"
                    />
                </View>
                <View style={styles.halfWidth}>
                    <Input
                        testID={`form-field-${field.fieldId}-last`}
                        title="Last Name"
                        placeholder="Last"
                        value={v.last ?? ''}
                        onUpdate={(text) => update('last', text)}
                        required={field.required}
                        disabled={disabled}
                        autoCapitalize="words"
                        autoComplete="family-name"
                    />
                </View>
            </View>
        </View>
    );
};

const styles = StyleSheet.create(({ spacing }) => ({
    container: {
        marginVertical: spacing.sm,
    },
    row: {
        flexDirection: 'row',
        gap: spacing.sm,
    },
    halfWidth: {
        flex: 1,
    },
}));

export default NameField;
